import lodash from 'lodash';
import { Model, Types } from '~app/core/model';
import DiagramNode from './DiagramNode';
import DiagramSignal from './DiagramSignal';

const DiagramState = Model.createType({
  name: 'StockFlowModelState',
  fields: {
    nodes: Types.MAPOF({
      keyType: Types.STR(),
      valueType: Types.MODEL(DiagramNode),
    }),
    signals: Types.LISTOF(Types.MODEL(DiagramSignal)),
  },
});

const validSignalTypes = {
  'stock-flow': true,
  'stock-variable': true,
  'stock-stock': true,
  'variable-flow': true,
  'variable-variable': true,
  'variable-stock': true,
  'flow-variable': true,
  'flow-flow': true,
  'flow-stock': true,
  'generic-generic': true,
};

function connectionsValid(signal, obj) {
  const src = obj.nodes[signal.connectFrom].type;
  const dst = obj.nodes[signal.connectTo].type;
  if (!validSignalTypes[`${src}-${dst}`]) {
    return [false, 'invalid-signal-connection', { src, dst }];
  }
  return [true];
}

const validators = [
  obj =>
    lodash.isObject(obj.nodes)
      ? [true]
      : [false, 'key-not-an-object', { key: 'nodes' }],
  obj =>
    lodash.isArray(obj.signals)
      ? [true]
      : [false, 'key-not-an-array', { key: 'signals' }],
  (obj, sfm) => {
    for (const n of Object.keys(obj.nodes)) {
      if (!sfm.labelsAsSet.has(n)) {
        return [false, 'no-such-label', { label: n }];
      }
      const node = obj.nodes[n];
      const [valid, ...err] = node.getIsValid(sfm);
      if (!valid) return [false, ...err];
      if (node.connectFrom && !obj.nodes[node.connectFrom]) {
        return [false, 'no-such-label', { label: node.connectFrom }];
      }
      if (node.connectTo && !obj.nodes[node.connectTo]) {
        return [false, 'no-such-label', { label: node.connectTo }];
      }
      if (
        (node.connectFrom && obj.nodes[node.connectFrom].type !== 'stock') ||
        (node.connectTo && obj.nodes[node.connectTo].type !== 'stock')
      ) {
        return [false, 'invalid-flow-connection'];
      }
    }
    return [true];
  },
  (obj, sfm) => {
    for (const signal of obj.signals) {
      const [valid, ...err] = signal.getIsValid(sfm);
      if (!valid) return [false, ...err];
      if (!obj.nodes[signal.connectFrom]) {
        return [false, 'no-such-label', { label: signal.connectFrom }];
      }
      if (!obj.nodes[signal.connectTo]) {
        return [false, 'no-such-label', { label: signal.connectTo }];
      }
    }
    for (const signal of obj.signals) {
      const [valid, ...err] = connectionsValid(signal, obj);
      if (!valid) return [false, ...err];
    }
    const signalsSet = new Set(
      obj.signals.map(s => `${s.connectFrom} ${s.connectTo}`),
    );
    if ([...signalsSet].length !== obj.signals.length) {
      return [false, 'duplicate-signals'];
    }
    return [true];
  },
];

DiagramState.prototype.getIsValid = function (sfm) {
  for (const v of validators) {
    const [result, ...err] = v(this, sfm);
    if (!result) {
      return [result, ...err];
    }
  }
  return [true];
};

export const signalConnectionsValid = function (signal, state) {
  return connectionsValid(signal, state)[0];
};

DiagramState.empty = new DiagramState({
  nodes: {},
  signals: [],
});

export default DiagramState;
